import axios from 'axios';

import { API_CONFIG } from 'constants/config';
import {
  apiMessages,
  apiGetMessage,
  apiGetMessageFiles,
  apiGetMessageBulkFiles,
  apiDownloadMessageFile,
  apiBulkMessage,
  apiBulkUpdateReadMessage,
  apiBulkUpdateArchivedMessage,
} from 'constants/apiRouting';
import { handleErrors } from 'utils/api';
import { dataURLtoFile } from 'utils/misc';
import { createActionType } from 'utils/redux';
import { getCreateMessageEvent } from 'constants/analytics';
import { apiGetFullThreadMessage } from '../../constants/apiRouting';
import { analyticsTrack } from '../../utils/analytics';


// object
export const CREATE_MESSAGE_PENDING = 'CREATE_MESSAGE_PENDING';
export const CREATE_MESSAGE_FAIL = 'CREATE_MESSAGE_FAIL';
export const CREATE_MESSAGE_SUCCESS = 'CREATE_MESSAGE_SUCCESS';
export const GET_MESSAGE_PENDING = 'GET_MESSAGE_PENDING';
export const GET_MESSAGE_FAIL = 'GET_MESSAGE_FAIL';
export const UPLOAD_IMAGE_PENDING = 'UPLOAD_IMAGE_PENDING';
export const UPLOAD_IMAGE_FAIL = 'UPLOAD_IMAGE_FAIL';
export const UPLOAD_IMAGE_SUCCESS = 'UPLOAD_IMAGE_SUCCESS';
export const GET_MESSAGES_PENDING = 'GET_MESSAGES_PENDING';
export const GET_MESSAGES_FAIL = 'GET_MESSAGES_FAIL';
export const GET_MESSAGES_SUCCESS = 'GET_MESSAGES_SUCCESS';
export const RESET_MESSAGE = 'RESET_MESSAGE';
export const GET_MESSAGE = createActionType('GET_MESSAGE');
export const TOGGLE_INBOX_DETAIL = 'TOGGLE_INBOX_DETAIL';
export const SET_AUTO_CATEGORIZED_DATE_MESSAGE = 'SET_AUTO_CATEGORIZED_DATE_MESSAGE';
export const SET_IMG_URL_SIZE_MAP = 'SET_IMG_URL_SIZE_MAP';
export const UPDATE_MESSAGE = createActionType('UPDATE_MESSAGE');
export const DESTROY_MESSAGE = createActionType('DESTROY_MESSAGE');
export const UPDATE_MESSAGE_READ_BULK = createActionType('UPDATE_MESSAGE_READ_BULK');
export const UPDATE_MESSAGE_ARCHIVED_BULK = createActionType('UPDATE_MESSAGE_ARCHIVED_BULK');
export const GET_MESSAGE_FILES = createActionType('GET_MESSAGE_FILES');
export const GET_MESSAGE_FULL_THREAD = createActionType('GET_MESSAGE_FULL_THREAD');
export const GET_FULL_MESSAGE = createActionType('GET_FULL_MESSAGE');


export const setAutoCategorizedDateMessage = ({ messageId, autoCategorizedDate }) => ({
  type: SET_AUTO_CATEGORIZED_DATE_MESSAGE,
  payload: {
    messageId,
    autoCategorizedDate,
  },
});


export const createMessage = (params = {
  sent_to: null,
  cc: null,
  body_html: null,
  outreach: null,
  in_reply_to: null,
  files: null,
}, type) => async (dispatch) => {
  dispatch({
    type: CREATE_MESSAGE_PENDING,
    payload: {},
  });
  try {
    const response = await axios.post(
      `${API_CONFIG.API_BASE}${API_CONFIG.API_VERSIONS.V1}messages/`,
      params, { withCredentials: true },
    );

    analyticsTrack(
      getCreateMessageEvent(type),
      {
        ...params,
        type,
      }, true,
    );
    dispatch({
      type: CREATE_MESSAGE_SUCCESS,
      payload: response.data,
    });
  } catch (error) {
    handleErrors(error, dispatch);
  }
};

export const resetMessage = () => (dispatch) => {
  dispatch({
    type: RESET_MESSAGE,
    payload: {},
  });
};

export const getMessage = messageId => async (dispatch) => {
  dispatch({
    type: GET_MESSAGE.name,
    payload: axios.get(
      apiGetMessage(messageId),
      { withCredentials: true },
    ).catch(error => handleErrors(error, dispatch)),
  });
};

export const getFullMessage = messageId => async (dispatch) => {
  dispatch({
    type: GET_FULL_MESSAGE.name,
    payload: axios.get(
      apiGetFullThreadMessage(messageId),
      { withCredentials: true },
    ).catch(error => handleErrors(error, dispatch)),
    meta: { messageId },
  });
};

export const downloadFile = (params = {
  file: null,
}) => async () => {
  axios.get(
    apiDownloadMessageFile(params.messageId),
    { params: { file: params.file }, withCredentials: true },
  );
};


export const uploadImage = async (params = {
  fileBase64: null,
}) => {
  try {
    const fileExtension = params.fileBase64.substring('data:image/'.length, params.fileBase64.indexOf(';base64'));
    const file = dataURLtoFile(params.fileBase64, `filename.${fileExtension}`);
    const data = new FormData();
    data.append('file', file, file.name);
    const response = await axios.post(
      `${API_CONFIG.API_BASE}${API_CONFIG.API_VERSIONS.V1}images/`,
      data, { withCredentials: true },
    );
    return response.data;
  } catch (error) {
    handleErrors(error);
    return { };
  }
};

export const getMessages = (
  params = {
    statuses: null,
    account__id: null,
    campaign__id: null,
    end_date: null,
    start_date: null,
    campaign__campaign_manager__id: null,
    page: null,
  },
  seamless = false,
  cancelToken,
) => async (dispatch) => {
  dispatch({
    type: GET_MESSAGES_PENDING,
    payload: seamless ? { ...params, loading: false, loaded: true } : params,
  });

  try {
    const paramsCopy = { ...params };
    const urlEncodedParams = new URLSearchParams(paramsCopy);
    const response = await axios.get(apiMessages(), {
      params: urlEncodedParams,
      withCredentials: true,
      cancelToken,
    });
    dispatch({
      type: GET_MESSAGES_SUCCESS,
      payload: response.data,
    });
  } catch (error) {
    if (!axios.isCancel(error)) {
      handleErrors(error, dispatch);
    }
  }
};

export const resetMessages = () => (dispatch) => {
  dispatch({
    type: GET_MESSAGES_SUCCESS,
    payload: { results: [] },
  });
};

export const toggleInboxDetail = () => (dispatch) => {
  dispatch({
    type: TOGGLE_INBOX_DETAIL,
    payload: {},
  });
};

export const patchMessage = (messageId, data = {}) => (dispatch) => {
  dispatch({
    type: UPDATE_MESSAGE.name,
    payload: axios.patch(
      apiGetMessage(messageId),
      data,
      { withCredentials: true },
    ).catch(error => handleErrors(error, dispatch)),
  });
};

export const getMessageFiles = messageId => (dispatch) => {
  dispatch({
    type: GET_MESSAGE_FILES.name,
    payload: axios.get(
      apiGetMessageFiles(messageId),
      { withCredentials: true },
    ).catch(error => handleErrors(error, dispatch)),
  });
};

export const destroyMessage = (messageId, refresh) => async (dispatch) => {
  if (refresh) {
    dispatch({
      type: DESTROY_MESSAGE.name,
      payload: axios.delete(
        apiGetMessage(messageId),
        { withCredentials: true },
      ).catch(error => handleErrors(error, dispatch)),
    });
  } else {
    try {
      await axios.delete(
        apiGetMessage(messageId),
        { withCredentials: true },
      );
    } catch (error) {
      handleErrors(error, dispatch);
    }
  }
};

export const getMessageBulkFiles = messageIds => async (dispatch) => {
  await dispatch({
    type: GET_MESSAGE_FILES.name,
    payload: axios.post(
      apiGetMessageBulkFiles(),
      messageIds,
      { withCredentials: true },
    ).catch(error => handleErrors(error, dispatch)),
  });
};

export const setMessageAsReadBulk = params => async (dispatch) => {
  await dispatch({
    type: UPDATE_MESSAGE_READ_BULK.name,
    payload: axios.post(
      apiBulkUpdateReadMessage(),
      params,
      { withCredentials: true },
    ).catch(error => handleErrors(error, dispatch)),
  });
};

export const setMessageArchivedBulk = params => async (dispatch) => {
  await dispatch({
    type: UPDATE_MESSAGE_ARCHIVED_BULK.name,
    payload: axios.post(
      apiBulkUpdateArchivedMessage(),
      params,
      { withCredentials: true },
    ).catch(error => handleErrors(error, dispatch)),
  });
};

export const setMessage = message => async (dispatch) => {
  await dispatch({
    type: GET_MESSAGE.name,
    payload: message,
  });
};


export const destroyBulkMessage = outreachIds => async (dispatch) => {
  try {
    await axios.delete(
      apiBulkMessage(),
      { withCredentials: true, data: outreachIds },
    );
  } catch (error) {
    handleErrors(error, dispatch);
  }
};

export const dispatchError = error => async (dispatch) => {
  /* eslint-disable-next-line no-param-reassign */
  error = {
    message: error.message,
    log: error.log,
    appError: true,
    response: {},
  };
  handleErrors(error, dispatch);
};
